import React from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons'
import IncomeUnitType from '../../../types/unit/IncomeUnit'
import { exactMatcher, getFormErrorMessageByName, hasError, useFormErrorContext } from '../../../utils/context/FormErrorContext'
import { useReadOnly } from '../../../utils/ReadOnlyContext'
import AIRPNumberInput from '../AIRPNumberInput/AIRPNumberInput'
import Button from '../Button/Button'
import Dropdown, { Option } from '../Dropdown/Dropdown'
import { UnitColumn } from './UnitTable'
import DatePicker from '../DatePicker/DatePicker'
import { format } from 'date-fns';
import Input from '../Input/Input'
import ToolTip from '../ToolTip/ToolTip'

interface BasicUnitRendererProps {
    units: IncomeUnitType[]
    columns: UnitColumn[]
    onUnitDelete: (unit: IncomeUnitType) => void
    onUnitChange: (unit: IncomeUnitType, column: UnitColumn | string, value: number | string | null | undefined | boolean) => void
}

function BasicUnitRenderer({units, columns, onUnitDelete, onUnitChange}: BasicUnitRendererProps) {
    const { isReadOnly } = useReadOnly()
    const { errors } = useFormErrorContext()
    
    const { t } = useTranslation(["suite_breakdown_table", "common"]);

    const castValue = (column: UnitColumn, value: undefined | null | string | boolean | number) => {
        if(value === undefined || value === null) {
            return value
        }

        const type = column.castedTo ? column.castedTo : column.type || "string"
        switch(type) {
            case 'string':
                return String(value)
            case 'number': {
                const result = Number(String(value))
                return Number.isFinite(result) ? result : null
            }
            case 'currency': {
                const result = Number(String(value))
                return Number.isFinite(result) ? result : null
            }
            case 'integer': {
                const result = Number(String(value))
                return Number.isFinite(result) ? result : null
            }
        }
        return 
    }

    const handleDateChange = (date: Date | null | undefined, name: string) => {
        if (typeof name !== "string") {
            return;
        }
        const nameArr = name.split(".")
        if (nameArr.length === 2) {
            const componentId = nameArr[0]
            const column = nameArr[1]
            const unit = units.find((u) => u.componentId === componentId)
            if (unit !== undefined) {
                if (date) {
                    const dateStr = format(date, "yyyy-MM-dd")
                    onUnitChange(unit, column, dateStr);
                } else {
                    onUnitChange(unit, column, undefined);
                }
            }
        }
        
    };

    const generateCellInputByUnit = (unit: IncomeUnitType, column: UnitColumn): JSX.Element | null => {
        const type = column.dropdown ? "dropdown" : column.type
        let value = unit[column.columnName as keyof IncomeUnitType];
        let generatedCellInput: JSX.Element | null = null;
        let options: Option[] = []
        if(type) {
            switch(type) {
                case 'dropdown': {
                    value = value !== undefined && value !== null ? String(value) : "";
                    options = column.dropdownSourceColumn ? ((column.dropdown as Record<string, Option[]>)[String(unit[column.dropdownSourceColumn as keyof IncomeUnitType])]) || [{ value: "", text: "" }]: column.dropdown as Option[];
                    generatedCellInput = (<>
                        <Dropdown
                            onChange={(e) => onUnitChange(unit, column, castValue(column, (e as React.ChangeEvent<HTMLSelectElement>).target.value))}
                            value={value}
                            options={options}
                            name={column.columnName}
                            hideErrorCross
                            errorMessage={getFormErrorMessageByName(errors, unit.componentId as string + "." + column.columnName, exactMatcher)}
                            ariaLabel={column.columnName + "Dropdown"}
                        />
                        {
                            // added invisible analysis fields for unit test purpose only
                            column.columnName === "analysisFlag" &&
                            <>
                                <div className="display-none" aria-label="analysisReviewedBy">{unit.analysisReviewedBy}</div>
                                <div className="display-none" aria-label="analysisReviewedDate">{unit.analysisReviewedDate}</div>
                            </>
                        }
                    </>);
                    break;
                }
                case 'number': {
                    generatedCellInput =  <AIRPNumberInput
                            formatType="decimal"
                            onChange={(value: number | null) => onUnitChange(unit, column, castValue(column, value))}
                            initialValue={value as number}
                            name={`${unit.componentId}.${column.columnName}`}
                            errorMatcher={exactMatcher}
                        />
                    break;
                }
                case 'currency': {
                    generatedCellInput = <AIRPNumberInput
                            formatType="currency"
                            symbol="$"
                            onChange={(value: number | null) => onUnitChange(unit, column, castValue(column, value))}
                            initialValue={value as number}
                            name={`${unit.componentId}.${column.columnName}`}
                            errorMatcher={exactMatcher}
                        />
                    break;
                }
                case 'integer': {
                    generatedCellInput = <AIRPNumberInput
                            formatType="integer"
                            onChange={(value: number | null) => onUnitChange(unit, column, castValue(column, value))}
                            initialValue={value as number}
                            name={`${unit.componentId}.${column.columnName}`}
                            errorMatcher={exactMatcher}
                        />
                    break;
                }
                case 'boolean': {
                    generatedCellInput = <input
                                readOnly={isReadOnly}
                                disabled={isReadOnly}
                                type="checkbox"
                                checked={Boolean(value)}
                                aria-label={column.columnName}
                                onChange={() => onUnitChange(unit, column, !value)} />
                    break;
                }
                case 'string': {
                    value = value !== undefined && value !== null ? String(value) : "";
                    generatedCellInput = <Input
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                            type="text"
                            value={value}
                            ariaLabel={column.columnName} 
                            errorMessage={getFormErrorMessageByName(errors, `${unit.componentId}.${column.columnName}`, exactMatcher)}
                            isError={hasError(errors, `${unit.componentId}.${column.columnName}`)}
                            hideErrorCross
                            onChange={(e) => onUnitChange(unit, column, e.target.value)}
                            id={`${unit.componentId}.${column.columnName}`}
                            name={`${unit.componentId}.${column.columnName}`}
                            />
                    break;
                }
                case 'date': {
                    generatedCellInput = <DatePicker name={`${unit.componentId}.${column.columnName}`} id={`${unit.componentId}.${column.columnName}`}
                                date={value as string | undefined}
                                onChange={handleDateChange}
                                maxDate={column.maxDate}
                                readOnly={isReadOnly}
                                disabled={isReadOnly}
                                errorMessage={getFormErrorMessageByName(errors, `${unit.componentId}.${column.columnName}`, exactMatcher)}
                            />
                    break;
                }
                default:
                    break;
            }
            if (column.tooltipForValue) {
                let displayValue = value !== null && value !== undefined ? value as string : ""
                if (type === "dropdown"){
                    const matchingOption = options.find((option) => option.value === value)
                    displayValue = matchingOption ? matchingOption.text : ""
                }
                return displayValue === "" ? generatedCellInput : <ToolTip contents={generatedCellInput}>
                    {displayValue}
                </ToolTip>
            } else {
                return generatedCellInput
            }
        }

        return null
    }

    return (
        <>
            {units.map((unit) => (
                <tr key={`table-row-${unit.componentId}`}>
                    {columns.map((column) => (
                        <td 
                        className={`${getFormErrorMessageByName(errors, unit.componentId + "." + column.columnName, exactMatcher) ? "table-error" : ""}`} 
                        key={`${column.columnName}-cell-${unit.componentId}`}>
                            {generateCellInputByUnit(unit, column)}
                        </td>
                    ))}
                    <td className={isReadOnly ? "disabled-trash" : ""}>
                        <Button onClick={() => onUnitDelete(unit)} text={<FontAwesomeIcon icon={faTrashCan} className={"trash"} />} ariaLabel={t("remove") + " " + unit.componentId} disabled={isReadOnly} />
                    </td>
                </tr>
            ))}
        </>
    )
}

export default BasicUnitRenderer